import React, { useContext, useState } from 'react';
import Isvg from 'react-inlinesvg';
import { useSelector } from 'react-redux';

import Message from 'components/Message';
import Star from 'components/Svgs/Star';
import Tabs, { Tab, TabLabel } from 'components/Tabs';
import { PFE_ICONS } from 'constants/icons';
import { bem } from 'helpers/class-name';
import Segment from 'helpers/segment';
import { selectActiveVenueId } from 'selectors/general';
import { selectIsDesktop } from 'selectors/ui';

import VenueSchedulesContext from '../Contexts/VenueSchedulesContext';
import AllBookingsSection from './AllBookingsSection';
import EditActionsMenu from './EditActionsMenu';
import EmailActionsMenu from './EmailActionsMenu';
import InsightsTabContent from './InsightsTabContent';
import MobileActionsOverflow from './MobileActionsOverflow';
import RosterTabContent from './RosterTabContent';
import { DETAIL_TAB, getDefaultDetailTab } from './ScheduleDetailV2.helpers';
import { useClassRatingStats } from './ScheduleDetailV2.hooks';
import messages from './ScheduleDetailV2.messages';
import './ScheduleDetailV2.scss';

const { block, element } = bem('schedule-detail-v2');

const SEGMENT_TAB_CHANGED = 'Schedule detail tab changed';

const ScheduleDetailV2 = () => {
  const { activeSchedule } = useContext(VenueSchedulesContext);
  const { duration, isInThePast, resource, scheduleClass, scheduleId, scheduleName, startDateTime } = activeSchedule;
  const [selectedTab, setSelectedTab] = useState(() => getDefaultDetailTab(activeSchedule));
  const isDesktop = useSelector(selectIsDesktop);
  const venueId = useSelector(selectActiveVenueId);
  const { data: rating } = useClassRatingStats(scheduleClass?.classId);

  const instructorName = resource?.resourceName || <Message message={messages.noInstructor} />;

  const handleTabChange = tab => {
    if (tab === selectedTab) return;
    Segment.event(SEGMENT_TAB_CHANGED, { venueId, scheduleId, tab });
    setSelectedTab(tab);
  };

  return (
    <section className={block({ isInThePast })} data-cypress="schedule-detail-v2">
      <header className={element('header')}>
        <div className={element('header-info')}>
          <h2 className={element('name', { isInThePast })} title={scheduleName}>
            {scheduleName}
          </h2>
          <p className={element('subtitle', { isInThePast })}>
            <Message
              message={messages.timeDurationAndInstructor}
              values={{ duration, instructorName, startDateTime }}
            />
          </p>
          {Boolean(rating?.count) && (
            <p className={element('rating', { isInThePast })} data-cypress="schedule-detail-v2-rating">
              <Message format="one-decimal" value={rating.value} />
              <Star className={element('rating-star')} />
              <Message message={messages.ratingCount} values={{ count: rating.count }} />
            </p>
          )}
        </div>
        <div className={element('header-actions')}>
          {isDesktop ? (
            <>
              <EmailActionsMenu schedule={activeSchedule} />
              <EditActionsMenu schedule={activeSchedule} />
            </>
          ) : (
            <MobileActionsOverflow schedule={activeSchedule} />
          )}
        </div>
      </header>
      <Tabs
        className={element('tabs')}
        selectedTab={selectedTab}
        onTabChange={handleTabChange}
        tabListProps={{ className: element('tabs-list') }}>
        <Tab name={DETAIL_TAB.INSIGHTS} className={element('tab')}>
          <TabLabel>
            <span className={element('tab-label')}>
              <Isvg className={element('tab-icon')} src={PFE_ICONS.insights} />
              <Message message={messages.tabInsights} />
            </span>
          </TabLabel>
          <AllBookingsSection schedule={activeSchedule} onViewInsights={() => handleTabChange(DETAIL_TAB.INSIGHTS)} />
          <InsightsTabContent schedule={activeSchedule} />
        </Tab>
        <Tab name={DETAIL_TAB.ROSTER} className={element('tab')}>
          <TabLabel>
            <span className={element('tab-label')}>
              <Isvg className={element('tab-icon')} src={PFE_ICONS.users} />
              <Message message={messages.tabRoster} />
            </span>
          </TabLabel>
          <RosterTabContent schedule={activeSchedule} />
        </Tab>
      </Tabs>
    </section>
  );
};

export default ScheduleDetailV2;
